<script setup>
import { computed } from "vue";

const props = defineProps({
  name: String,
  settings: Object,
  data: Object
})

const { $device } = useNuxtApp()

const parseSetting = (raw) => {
  let value = raw || {}

  try {
    if (typeof value === 'string') value = JSON.parse(value)
    if (typeof value === 'string') value = JSON.parse(value)
  } catch (e) {
    value = {}
  }

  return value && typeof value === 'object' ? value : {}
}

const formatValue = (v) => {
  if (v === undefined || v === null || v === '' || v === 0 || v === '0' || v === '0px') {
    return undefined
  }
  return isNaN(v) ? v : v + 'px'
}

const toCamelCase = (prop) => prop.replace(/-([a-z])/g, (_, char) => char.toUpperCase())

const parseCssDeclarations = (css) => {
  if (!css || typeof css !== 'string') {
    return {}
  }

  const style = {}

  css.split(';').forEach((chunk) => {
    const part = chunk.trim()
    if (!part) {
      return
    }

    const colonIndex = part.indexOf(':')
    if (colonIndex <= 0) {
      return
    }

    const property = part.slice(0, colonIndex).trim().toLowerCase()
    const value = part.slice(colonIndex + 1).trim()

    if (!property || !value) {
      return
    }

    // Block obviously dangerous values
    if (/expression|javascript:|url\s*\(\s*['"]?\s*javascript:/i.test(value)) {
      return
    }

    style[toCamelCase(property)] = value
  })

  return style
}

const deviceSettings = computed(() => {
  const rawMargins = parseSetting(props.settings?.margins)
  const rawMaxWidth = parseSetting(props.settings?.max_width)

  let rawStyles = props.settings?.styles
  if (typeof rawStyles === 'string' && !rawStyles.trim().startsWith('{')) {
    rawStyles = { desktop: rawStyles, mobile: '' }
  } else {
    rawStyles = parseSetting(rawStyles)
  }

  const desktopMargins = rawMargins.desktop || rawMargins
  const mobileMargins = rawMargins.mobile || desktopMargins
  const activeMargins = $device.isMobile ? mobileMargins : desktopMargins

  const desktopMaxWidth = rawMaxWidth.desktop ?? rawMaxWidth.value ?? ''
  const mobileMaxWidth = rawMaxWidth.mobile ?? desktopMaxWidth
  const activeMaxWidth = $device.isMobile ? mobileMaxWidth : desktopMaxWidth

  const desktopStyles = rawStyles.desktop ?? rawStyles.css ?? ''
  const mobileStyles = rawStyles.mobile ?? ''
  const activeStyles = $device.isMobile
    ? (mobileStyles || desktopStyles)
    : desktopStyles

  return {
    margins: {
      top: formatValue(activeMargins.top),
      bottom: formatValue(activeMargins.bottom),
      left: formatValue(activeMargins.left),
      right: formatValue(activeMargins.right),
    },
    maxWidth: formatValue(activeMaxWidth),
    customStyles: parseCssDeclarations(activeStyles),
  }
})

const wrapperStyle = computed(() => {
  const { margins, maxWidth, customStyles } = deviceSettings.value
  const style = { ...customStyles }

  if (margins.top) style.marginTop = margins.top
  if (margins.bottom) style.marginBottom = margins.bottom
  if (margins.left) style.marginLeft = margins.left
  if (margins.right) style.marginRight = margins.right

  if (maxWidth) {
    style.maxWidth = maxWidth
    style.width = style.width || '100%'
    if (!margins.left) {
      style.marginLeft = 'auto'
    }
    if (!margins.right) {
      style.marginRight = 'auto'
    }
  }

  return style
})

const pluginComponent = createLazyComponent(
  () => loadPlugin(props.name),
  `plugin:${props.name}`
)

onMounted(() => {
  if (props.name) {
    preloadOnIdle(() => preloadPlugin(props.name))
  }
})
</script>

<template>
  <div :class="{ container: settings?.inContainer }" :style="wrapperStyle">
    <component v-if="pluginComponent" :is="pluginComponent" :data="data" :settings="settings" />
  </div>
</template>

<style scoped lang="scss">
</style>
